// Single-flight access-token refresh, ported from mobile's refreshAccessToken.
// Every caller awaits the same in-flight round-trip so concurrent 401s don't
// each POST a refresh and invalidate one another under single-use rotation.
//
// This uses a raw fetch (not restClient) on purpose: restClient routes 401s
// back through here, so going through it would recurse.
import { API_V1 } from 'config';
import { getRefreshToken, setTokens, clearTokens } from './tokenStorage';

export type RefreshResult =
  | { ok: true; accessToken: string }
  | { ok: false; reason: 'invalid_refresh' | 'no_token' | 'network' };

const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 500;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

let inflightRefresh: Promise<RefreshResult> | null = null;

async function doRefresh(): Promise<RefreshResult> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    clearTokens();
    return { ok: false, reason: 'no_token' };
  }

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt += 1) {
    try {
      const res = await fetch(`${API_V1}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (res.ok) {
        const data = (await res.json()) as { access_token: string; refresh_token: string };
        setTokens(data.access_token, data.refresh_token);
        return { ok: true, accessToken: data.access_token };
      }

      // Any 4xx is a permanent rejection (malformed / expired / revoked).
      if (res.status >= 400 && res.status < 500) {
        clearTokens();
        return { ok: false, reason: 'invalid_refresh' };
      }
      // 5xx — fall through to retry/backoff.
    } catch {
      /* network error — fall through to retry/backoff */
    }

    if (attempt < MAX_RETRIES) {
      await sleep(RETRY_DELAY_MS * (attempt + 1));
    }
  }

  // Transient failure exhausted — keep tokens so a later attempt can recover.
  return { ok: false, reason: 'network' };
}

export function refreshAccessToken(): Promise<RefreshResult> {
  if (inflightRefresh) return inflightRefresh;
  inflightRefresh = doRefresh().finally(() => {
    inflightRefresh = null;
  });
  return inflightRefresh;
}
